import { colors } from '../../core/constants.js';
import { MinimalErrorTracker } from '../../shared/log.js';
import { getAllDefinitions, getUIDefinition } from '../../shared/specials-public.js';

const CSS_CLASS_PIECE_SHADOW = 'piece-shadow';
const CSS_CLASS_PIECE = 'piece';
const CELL_KEY_SEPARATOR = ',';
const PREVIEW_COMMON_CLASSES = ['piece--preview-special'];
const PREVIEW_TYPE_CLASS_MAP = {
  crossStripe: ['piece--preview-cross'],
  cross: ['piece--preview-cross'],
};
const PREVIEW_ATTRIBUTE = 'data-preview-active';

/**
 * @typedef {Object} Position
 * @property {number} row - 行番号
 * @property {number} col - 列番号
 */

/**
 * @typedef {Object} Piece
 * @property {number} id - 安定識別子（数値）
 * @property {string} color - ピースの色
 * @property {string} [specialType] - 特殊ピースタイプ
 * @property {Position} position - ピース位置
 */

/**
 * ボード描画専用クラス
 */
class BoardRenderer {
  /**
   * @param {HTMLTableElement} tableElement - ゲームボードテーブル
   */
  constructor(tableElement) {
    this.table = tableElement;
    this.root = null; // Board要素への参照（initialize()で設定）
    this.cellIndex = null; // 座標→要素のキャッシュ（任意）

    // 特殊タイプクラス定数
    this.specialTypeClasses = ['special', ...getAllDefinitions()];
    this.previewStates = new Map();
  }

  /**
   * Board要素の初期化（DOM参照の依存注入）
   * 多重呼び出しは警告を出す
   * @param {HTMLElement} boardElement - Board要素
   */
  initialize(boardElement) {
    if (!boardElement) return;

    // 多重初期化の検出と警告
    if (this.root) {
      MinimalErrorTracker.warn('RENDER', 'BoardRenderer.initialize() called multiple times');
      return;
    }

    this.root = boardElement;
    this._invalidateCellIndex();
  }

  /**
   * セルインデックスの無効化
   * DOM変更時（updatePieceAt, updatePieces, clearBoard）に必ず呼ぶこと
   * @private
   */
  _invalidateCellIndex() {
    this.cellIndex = null;
  }

  /**
   * セルインデックスの構築（遅延初期化）
   * @private
   */
  _ensureCellIndex() {
    if (this.cellIndex) return;

    this.cellIndex = new Map();
    if (!this.table) return;

    const wrappers = this.table.querySelectorAll(`.${CSS_CLASS_PIECE_SHADOW}`);
    for (const wrapper of wrappers) {
      const row = parseInt(wrapper.dataset.row, 10);
      const col = parseInt(wrapper.dataset.col, 10);
      if (!isNaN(row) && !isNaN(col)) {
        this.cellIndex.set(`${row}${CELL_KEY_SEPARATOR}${col}`, wrapper);
      }
    }
  }

  _getPreviewKey(row, col) {
    return `${row}${CELL_KEY_SEPARATOR}${col}`;
  }

  _resolvePreviewClasses(previewDescriptor) {
    const classes = [...PREVIEW_COMMON_CLASSES];
    if (!previewDescriptor) {
      return classes;
    }

    if (Array.isArray(previewDescriptor)) {
      previewDescriptor.forEach((cls) => {
        if (typeof cls === 'string' && cls.length > 0 && !classes.includes(cls)) {
          classes.push(cls);
        }
      });
      return classes;
    }

    if (typeof previewDescriptor === 'string') {
      const mapped = PREVIEW_TYPE_CLASS_MAP[previewDescriptor];
      if (Array.isArray(mapped)) {
        mapped.forEach((cls) => {
          if (typeof cls === 'string' && cls.length > 0 && !classes.includes(cls)) {
            classes.push(cls);
          }
        });
      }
      return classes;
    }

    if (typeof previewDescriptor === 'object' && Array.isArray(previewDescriptor.classes)) {
      previewDescriptor.classes.forEach((cls) => {
        if (typeof cls === 'string' && cls.length > 0 && !classes.includes(cls)) {
          classes.push(cls);
        }
      });
    }

    return classes;
  }

  _clearPreviewAt(row, col) {
    if (!Number.isInteger(row) || !Number.isInteger(col)) return;

    const key = this._getPreviewKey(row, col);
    const existing = this.previewStates.get(key);
    if (!existing) {
      return;
    }

    const piece = this.queryPieceInnerAt(row, col);
    if (piece) {
      existing.classes.forEach((cls) => piece.classList.remove(cls));
      piece.removeAttribute(PREVIEW_ATTRIBUTE);
      if (Array.isArray(existing.removedClasses)) {
        existing.removedClasses.forEach((cls) => piece.classList.add(cls));
      }
    }

    this.previewStates.delete(key);
  }

  beginPreviewAt(row, col, previewDescriptor) {
    if (!Number.isInteger(row) || !Number.isInteger(col)) return;

    const piece = this.queryPieceInnerAt(row, col);
    if (!piece) return;

    const classes = this._resolvePreviewClasses(previewDescriptor);
    if (classes.length === 0) return;

    this._clearPreviewAt(row, col);

    const removedClasses = [];
    const removable = new Set(['special', ...this.specialTypeClasses]);
    removable.forEach((cls) => {
      if (!cls) return;
      if (piece.classList.contains(cls)) {
        piece.classList.remove(cls);
        removedClasses.push(cls);
      }
    });

    classes.forEach((cls) => piece.classList.add(cls));
    piece.setAttribute(PREVIEW_ATTRIBUTE, 'true');

    const key = this._getPreviewKey(row, col);
    this.previewStates.set(key, { row, col, classes, removedClasses });
  }

  endPreviewAt(row, col) {
    this._clearPreviewAt(row, col);
  }

  clearAllPreviews() {
    for (const entry of this.previewStates.values()) {
      const { row, col, classes, removedClasses } = entry;
      const piece = this.queryPieceInnerAt(row, col);
      if (piece) {
        classes.forEach((cls) => piece.classList.remove(cls));
        piece.removeAttribute(PREVIEW_ATTRIBUTE);
        if (Array.isArray(removedClasses)) {
          removedClasses.forEach((cls) => piece.classList.add(cls));
        }
      }
    }
    this.previewStates.clear();
  }

  /**
   * ピースを描画
   * @private
   * @param {Piece} piece - ピースデータ
   * @param {Position} position - 描画位置
   * @param {number} [initialOffset=0] - アニメーション用初期Y位置オフセット
   * @param {boolean} [forCreation=false] - 特殊ピース生成フラグ（trueの場合のみ初期透明化）
   */
  _renderPiece(piece, position, initialOffset = 0, forCreation = false) {
    // シンプルな作成のみ
    const element = this._createElement(position);
    const pieceDiv = element.querySelector(`.${CSS_CLASS_PIECE}`);

    // 要素の状態を確実にリセット
    pieceDiv.className = CSS_CLASS_PIECE;
    pieceDiv.style.cssText = '';

    // 色クラスの更新
    colors.forEach((c) => pieceDiv.classList.remove(c));
    pieceDiv.classList.add(piece.color);

    // 特殊タイプクラスの更新
    this.specialTypeClasses.forEach((cls) => pieceDiv.classList.remove(cls));
    if (piece.specialType) {
      const uiDef = getUIDefinition(piece.specialType);
      if (uiDef?.cssClasses) {
        uiDef.cssClasses.forEach((cls) => pieceDiv.classList.add(cls));
      }
    }

    // データ属性の更新
    if (piece?.id != null) {
      pieceDiv.setAttribute('data-id', String(piece.id));
    }

    // 初期位置オフセット設定
    if (initialOffset !== 0) {
      element.style.transform = `translate3d(0px, ${initialOffset}px, 0)`;
      element.style.willChange = 'transform';
    }

    // 特殊ピース生成時のみ初期透明化
    // forCreation フラグは executeSpecialCreationAnimation からのみ渡される
    // 理由: 重力落下、変換などの通常更新では透明化不要
    //       真の「生成」のみ透明化し、creationFX.finishCreationBurst で可視化
    if (forCreation && piece && piece.specialType) {
      pieceDiv.style.visibility = 'hidden';
      pieceDiv.style.opacity = '0';
    }
  }

  /**
   * ピースを削除
   * @private
   * @param {Position} position - 削除位置
   */
  _removePiece(position) {
    const cell = this.table.rows[position.row].cells[position.col];
    const shadowElement = cell.querySelector(`.${CSS_CLASS_PIECE_SHADOW}`);

    if (shadowElement) {
      const pieceDiv = shadowElement.querySelector(`.${CSS_CLASS_PIECE}`);
      if (pieceDiv) {
        // ピース要素のスタイルとクラスをリセット
        pieceDiv.style.cssText = '';
        pieceDiv.className = CSS_CLASS_PIECE;

        // 色クラスと特殊タイプクラスを削除
        colors.forEach((c) => pieceDiv.classList.remove(c));
        this.specialTypeClasses.forEach((cls) => pieceDiv.classList.remove(cls));
      }

      // Shadow要素も完全にクリア
      shadowElement.remove();
    }
  }

  /**
   * ピースを更新
   * @private
   * @param {Piece} piece - ピースデータ
   * @param {Position} position - 更新位置
   * @param {number} [initialOffset=0] - アニメーション用初期Y位置オフセット
   * @param {boolean} [forCreation=false] - 特殊ピース生成フラグ
   */
  _updatePiece(piece, position, initialOffset = 0, forCreation = false) {
    // 更新は削除→追加として実装
    this._removePiece(position);
    this._renderPiece(piece, position, initialOffset, forCreation);

    // レンダー後に.piece-shadowクラスが設定されていることを確認
    const element = this.getElement(position.row, position.col);
    if (element && !element.classList.contains(CSS_CLASS_PIECE_SHADOW)) {
      element.classList.add(CSS_CLASS_PIECE_SHADOW);
    }
    // 特殊ピース生成アニメーションは正規フローで処理
  }

  /**
   * 新しいピース要素を作成
   * @private
   * @param {Position} position - 位置
   * @returns {HTMLElement} 作成された要素
   */
  _createElement(position) {
    const cell = this.table.rows[position.row].cells[position.col];
    const shadowDiv = document.createElement('div');
    const pieceDiv = document.createElement('div');
    shadowDiv.classList.add('piece-shadow');
    shadowDiv.setAttribute('data-row', position.row);
    shadowDiv.setAttribute('data-col', position.col);

    pieceDiv.classList.add(CSS_CLASS_PIECE);
    pieceDiv.setAttribute('data-row', position.row);
    pieceDiv.setAttribute('data-col', position.col);

    shadowDiv.appendChild(pieceDiv);
    cell.appendChild(shadowDiv);

    return shadowDiv;
  }

  /**
   * 単一ピースを直接更新
   * @param {number} row - 行番号
   * @param {number} col - 列番号
   * @param {Piece} piece - ピースデータ
   * @param {Object} [options={}] - オプション
   * @param {number} [options.initialOffset] - 初期Y位置オフセット
   * @param {boolean} [options.forCreation] - 特殊ピース生成フラグ
   */
  updatePieceAt(row, col, piece, options = {}) {
    this._clearPreviewAt(row, col);
    this._updatePiece(
      piece,
      { row, col },
      options.initialOffset || 0,
      options.forCreation || false,
    );
    // DOM変更後に必ずキャッシュ無効化
    this._invalidateCellIndex();
  }

  /**
   * 複数ピースを一括更新
   * @param {Piece[]} pieces - ピース配列
   * @param {Object} [options={}] - オプション
   */
  updatePieces(pieces, options = {}) {
    const { initialOffsets = new Map() } = options;

    for (const piece of pieces) {
      const { row, col } = piece.position;
      const key = `${row},${col}`;
      const initialOffset = initialOffsets.get(key) || 0;
      this._clearPreviewAt(row, col);
      this._updatePiece(piece, { row, col }, initialOffset);
    }
    // 一括DOM変更後に必ずキャッシュ無効化
    this._invalidateCellIndex();
  }

  /**
   * 指定位置のピースを削除
   * @param {number} row - 行番号
   * @param {number} col - 列番号
   */
  removePieceAt(row, col) {
    this._clearPreviewAt(row, col);
    this._removePiece({ row, col });
  }

  /**
   * ボード全体をクリア
   */
  clearBoard() {
    try {
      this.clearAllPreviews();
      const shadows = this.table.querySelectorAll(`.${CSS_CLASS_PIECE_SHADOW}`);
      shadows.forEach((s) => s.remove());
      // ボードクリア後に必ずキャッシュ無効化
      this._invalidateCellIndex();
    } catch (error) {
      MinimalErrorTracker.warn('RENDER', 'Failed to clear board', error);
    }
  }

  /**
   * 座標からピースラッパー要素を取得（読み取り専用）
   * @param {number} row - 行
   * @param {number} col - 列
   * @returns {HTMLElement|undefined}
   */
  queryPieceWrapperAt(row, col) {
    if (!this.table || !Number.isInteger(row) || !Number.isInteger(col)) {
      return undefined;
    }

    // キャッシュ戦略: あればキャッシュから、なければクエリ
    this._ensureCellIndex();
    const key = `${row},${col}`;
    const cached = this.cellIndex.get(key);

    // キャッシュヒット時は接続確認（破棄済みノード対策）
    if (cached) {
      if (cached.isConnected) {
        return cached;
      }
      // 破棄済みの場合はキャッシュから削除
      this.cellIndex.delete(key);
    }

    // フォールバック: 直接クエリ
    const wrapper = this.table.querySelector(`[data-row="${row}"][data-col="${col}"]`);

    // 新規取得したノードをキャッシュに追加
    if (wrapper) {
      this.cellIndex.set(key, wrapper);
    }

    return wrapper || undefined;
  }

  /**
   * 座標からピース内部要素を取得（読み取り専用）
   * @param {number} row - 行
   * @param {number} col - 列
   * @returns {HTMLElement|undefined}
   */
  queryPieceInnerAt(row, col) {
    const wrapper = this.queryPieceWrapperAt(row, col);
    if (!wrapper) return undefined;

    const inner = wrapper.querySelector('.piece');
    return inner || undefined;
  }

  /**
   * 複数座標からピースラッパー要素を一括取得（読み取り専用）
   * @param {Array<{row: number, col: number}>} positions - 座標配列
   * @returns {Array<HTMLElement>} - 見つかった要素の配列
   */
  queryPieceWrappersByPositions(positions) {
    if (!Array.isArray(positions)) return [];

    return positions
      .map((pos) => this.queryPieceWrapperAt(pos.row, pos.col))
      .filter((el) => el !== undefined);
  }

  /**
   * ラッパー要素から内部ピース要素を取得（読み取り専用）
   * @param {HTMLElement} wrapper - ラッパー要素
   * @returns {HTMLElement|undefined}
   */
  queryPieceInner(wrapper) {
    if (!wrapper || !wrapper.querySelector) return undefined;

    const inner = wrapper.querySelector('.piece');
    return inner || undefined;
  }

  /**
   * Board要素を取得（確定的な参照取得）
   * @returns {HTMLElement|undefined}
   */
  getRoot() {
    return this.root || undefined;
  }

  /**
   * すべてのピースラッパー要素を取得（読み取り専用）
   * @returns {Array<HTMLElement>}
   */
  queryAllPieceWrappers() {
    if (!this.table) return [];

    const wrappers = this.table.querySelectorAll(`.${CSS_CLASS_PIECE_SHADOW}`);
    return Array.from(wrappers);
  }

  /**
   * すべてのピース内部要素を取得（読み取り専用）
   * @returns {Array<HTMLElement>}
   */
  queryAllPieceInners() {
    if (!this.table) return [];

    const inners = this.table.querySelectorAll('.piece-shadow .piece');
    return Array.from(inners);
  }

  /**
   * 指定位置のピース要素を取得
   * @param {number} row - 行番号
   * @param {number} col - 列番号
   * @returns {HTMLElement|null} ピース要素
   */
  getElement(row, col) {
    return this.table.rows[row].cells[col].querySelector(`.${CSS_CLASS_PIECE_SHADOW}`);
  }
}

// シングルトンインスタンスを作成
let boardRendererInstance = null;

function getBoardRenderer() {
  if (!boardRendererInstance) {
    const table = document.getElementById('table');
    if (table) {
      boardRendererInstance = new BoardRenderer(table);
    }
  }
  return boardRendererInstance;
}

export { BoardRenderer, getBoardRenderer };
